import type { WorkItemSummary } from '../api/WorkApi.ts';

/**
 * The live protocol, mirroring the server (api/src/modules/realtime/protocol.ts).
 * Every project event carries `projectId`. The tab that caused a change gets it
 * with `own: true`: a confirmation, applied without the "changed" highlight.
 */
export const LIVE = {
  itemsUpserted: 'items:upserted',
  itemsDeleted: 'items:deleted',
  itemSection: 'item:section',
  timeline: 'timeline',
  sprintsChanged: 'sprints:changed',
  projectChanged: 'project:changed',
  projectRevoked: 'project:revoked',
  editing: 'presence:editing',
  editingStopped: 'presence:stopped',
} as const;

/** Events about the signed-in person rather than a project. */
export const PERSONAL = {
  /** Your notifications changed (new, or read in another tab): refetch counts. */
  notifications: 'notifications:changed',
} as const;

export type ItemSection =
  | 'checklist'
  | 'links'
  | 'attachments'
  | 'watchers'
  | 'subtasks'
  | 'claim'
  /** Agents' questions and deletion requests about the item. */
  | 'questions';
export type ProjectPart =
  | 'statuses'
  | 'labels'
  | 'members'
  | 'settings'
  | 'sprint-settings'
  /** What agents are doing (sessions, journals). */
  | 'agents'
  /** The project's whiteboards. */
  | 'boards'
  /** The Docs tree (folders and pages), or a page's text. */
  | 'docs'
  /** Decisions: asked, changed, voted, decided or revised (the tab, its badge, blockers). */
  | 'decisions';

export interface GoneItem {
  id: string;
  number: number;
  movedTo: string | null;
}

/** What a project subscription hears (payloads as the server sends them). */
export interface ProjectEvents {
  [LIVE.itemsUpserted]: { items: WorkItemSummary[]; own?: boolean };
  [LIVE.itemsDeleted]: { items: GoneItem[] };
  [LIVE.itemSection]: { itemId: string; section: ItemSection };
  [LIVE.timeline]: { targetType: 'item' | 'sprint' | 'page' | 'decision'; targetId: string };
  [LIVE.sprintsChanged]: Record<string, never>;
  [LIVE.projectChanged]: { part: ProjectPart };
  [LIVE.editing]: { itemId: string; field: string; user: { id: string; name: string } };
  [LIVE.editingStopped]: { itemId: string; field: string; userId: string };
}

export type ProjectEventName = keyof ProjectEvents;
export const PROJECT_EVENTS = Object.values(LIVE).filter(
  (e): e is ProjectEventName => e !== LIVE.projectRevoked,
);
